import {
  Euler,
  Object3D,
  Object3DEventMap,
  OrthographicCamera,
  Quaternion,
  Scene,
  Vector3,
} from "three";
import { generateUUID } from "three/src/math/MathUtils.js";
import { SceneInfo } from "../api/types/Scene";
import { CameraInterface, Engine } from "../engine/Engine";
import { clearAssetCache } from "../loader/Loader";
import { clearCache } from "../loader/SugarModelLoader";
import { DEFAULT_ROOM } from "../util/GeometryUtil";
import {
  fixRoomClockwise,
  isEndOfALine,
  isPartOfARoom,
  isStartOfALine,
  reverseAllFollowingWalls,
  reverseAllPreviousWalls,
} from "../util/SceneUtil";
import { logger } from "../util/Logger";
import { Corner } from "./Corner";
import { Line } from "./Line";
import { Meter } from "./Meter";
import { Point } from "./Point";
import { Polygon } from "./Polygon";
import { SceneImportData } from "./types";
import { Balcony } from "./walls/Balcony";
import { BlankWall } from "./walls/BlankWall";
import { Wall } from "./walls/Wall";
import { ClassRegistry } from "../class/ClassRegistry";
import { captureDocument, applyDocument } from "../history/documentSnapshot";
import type { HistoryDir, SugarStateSlice } from "../history/SugarObject";
import { getStateRootByUuid, trackStateRoot } from "../history/SugarObject";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelConfigurator } from "../loader/SugarModelConfigurator";
import {
  collectConfiguratorLeafModels,
  uniqueSugarModels,
} from "../core/configurator/ConfiguratorTraversal";

type RoomShapeSnapshot = Pick<SceneImportData, "points" | "lines">;

interface RoomStateValue {
  products: string[];
  shape: RoomShapeSnapshot;
}

export class Room extends Point {
  private _models: Object3D[] = [];
  private readonly _stateRootUuid = generateUUID();
  private _raycastingTargets: Object3D[] = [];
  private _lineCreate: (e: Point, e2: Point) => Line;
  private _onLineCreate?: (e: Line) => void;
  private _onPolygonCrate?: (e?: Polygon) => void;

  constructor(
    lineCreate: (e: Point, e2: Point) => Line,
    onlineCreate?: (e: Line) => void,
    onPolygonCrate?: (e?: Polygon) => void
  ) {
    super();
    this._type = "Scene";
    this._name = "Scene_" + generateUUID();
    this._onLineCreate = onlineCreate;
    this._onPolygonCrate = onPolygonCrate;
    this._lineCreate = lineCreate;
    trackStateRoot(this);
  }

  get models(): Object3D[] {
    return this._models;
  }

  get uuid(): string {
    return this._stateRootUuid;
  }

  get state(): SugarStateSlice<RoomStateValue> {
    return {
      kind: "room",
      value: {
        products: uniqueSugarModels(this._models).map((m) => m.uuid),
        shape: this._captureShape(),
      },
    };
  }

  applyState(slice: SugarStateSlice, _dir: HistoryDir): void {
    if (slice.kind === "roomProducts") {
      this._applyProducts(slice.value as string[]);
      return;
    }
    if (slice.kind !== "room") return;
    const value = slice.value as RoomStateValue;
    this._applyShape(value.shape);
    this._applyProducts(value.products);
  }

  private _applyProducts(modelUuids: string[]): void {
    const desired = new Set(modelUuids);
    const current = uniqueSugarModels(this._models);

    for (const m of current) {
      if (desired.has(m.uuid)) continue;
      Engine.activeEngine?.activeScene?.remove(m);
      this.removeModel(m);
    }

    const inRoom = new Set(uniqueSugarModels(this._models).map((m) => m.uuid));
    for (const uuid of modelUuids) {
      if (inRoom.has(uuid)) continue;
      const model = getStateRootByUuid(uuid) as SugarModel | undefined;
      if (!model) continue;
      Engine.activeEngine?.activeScene?.add(model);
      this.addModel(model);
    }
  }

  private _captureShape(): RoomShapeSnapshot {
    const cornerNodes =
      this._childs?.filter((c): c is Corner => c instanceof Corner) ?? [];
    const points = cornerNodes.map((c) => ({
      x: c.point.x,
      y: c.point.y,
      z: c.point.z,
    }));
    const lines =
      this._childs
        ?.filter((c): c is Line => c instanceof Line)
        .map((line) => ({
          start: cornerNodes.findIndex((co) => co === line.start),
          end: cornerNodes.findIndex((co) => co === line.end),
          type: ClassRegistry.getNameOf(line.constructor)!,
          hidden: line.hidden,
        })) ?? [];
    return { points, lines };
  }

  private _clearShapeChildren(): void {
    const toRemove = [...(this._childs ?? [])];
    for (const child of toRemove) {
      child.removeFromParent();
      child.dispose();
    }
    this._childs = [];
    this._currentLine = undefined;
  }

  private _applyShape(shape: RoomShapeSnapshot): void {
    this._clearShapeChildren();
    if (!shape.points?.length && !shape.lines?.length) {
      this.updateUnfinishedCorners();
      return;
    }
    this._buildShapeFromData(shape);
    this.updateUnfinishedCorners();
  }

  private _buildShapeFromData(
    data: RoomShapeSnapshot,
    productInstances?: SceneImportData["productInstances"],
  ): void {
    const corners = data.points?.map((v) =>
      this._createCorner(new Vector3(v.x, v.y, v.z)),
    );

    const lines = data.lines?.map((l) => {
      const line = this.createLine(
        corners![l.start],
        corners![l.end],
        (pStart: Point, pEnd: Point) =>
          new (ClassRegistry.get(l.type) as any)(pStart, pEnd),
      );
      line.hidden = l.hidden;
      if (l.attributes) {
        for (const [key, value] of Object.entries(l.attributes)) {
          if (line instanceof Balcony && key === "profileId") {
            const productInstance = productInstances?.find((p) => p.id == value);
            if (productInstance) {
              line.profileData = { id: productInstance.productId };
            }
          } else if (line instanceof Balcony && key === "glassId") {
            const productInstance = productInstances?.find((p) => p.id == value);
            if (productInstance) {
              line.glassData = { id: productInstance.productId };
            }
          } else {
            line.setAttributes(key, value);
          }
        }
      }
      return line;
    });

    this.createPolygonFromLines(lines);
  }

  get root(): Object3D<Object3DEventMap> {
    const mesh = this.childs?.find((e) => e instanceof Polygon)?.root!;
    return mesh;
  }

  get raycastingTargets(): Object3D[] {
    const raycastingtargets = [...this._raycastingTargets];
    this._childs?.forEach((c) => {
      if (c.raycastable && c.visible)
        raycastingtargets.push(c.raycastingTarget!);
    });
    return raycastingtargets.filter((m) => m);
  }

  private _createCorner(pos: Vector3) {
    const corner = new Corner(pos);
    corner.renderMode = this._renderMode;
    this.addChild(corner);
    return corner;
  }

  createLine(
    start: Vector3 | Point,
    end: Vector3 | Point,
    tool?: (e: Point, e2: Point) => Line
  ) {
    let pStart;
    if (start instanceof Vector3) {
      pStart = this._createCorner(start);
    } else pStart = start;

    let pEnd;
    if (end instanceof Vector3) {
      pEnd = this._createCorner(end);
    } else pEnd = end;

    const line = tool ? tool(pStart, pEnd) : this._lineCreate(pStart, pEnd);
    line.start = pStart;
    line.end = pEnd;
    this.addChild(line);
    line.isDirty = true;
    line.renderMode = this._renderMode;

    return line;
  }

  createPolygonFromLines(lines?: Line[]) {
    if (!lines || !lines.length) return;

    const points = lines?.map((l) => {
      return l.start;
    });

    const polygon = new Polygon(points!);
    points!.forEach((p) => {
      polygon.unbindJoint(p);
      polygon.bindJoint(p);
    });
    polygon.joints = [...points!];
    this.addChild(polygon);
    polygon.isDirty = true;
    polygon.renderMode = this._renderMode;
    //this._childs?.push(polygon);
    this._childs?.forEach((c) => {
      c.isDirty = true;
      c.raycastable = false;
    });

    return polygon;
  }

  addModel(product: SugarModel) {
    this._models.push(product);
    this._raycastingTargets.push(product);
    (product as any).scene = this;
  }

  removeModel(product: SugarModel) {
    this._models = this._models.filter((m) => m != product);
    this._raycastingTargets = this._raycastingTargets.filter(
      (m) => m != product
    );
    (product as any).scene = undefined;
  }

  get currentLine(): Line {
    return this._currentLine!;
  }

  private _currentLine?: Line;

  //for continues
  addPoint(point: Vector3): Point | undefined {
    logger.info("addPPoint");
    const closesPoint = this.getClosestCorner(point, this._currentLine);
    if (!this._currentLine) {
      if (closesPoint) {
        this._currentLine = this.createLine(closesPoint, point);
      } else {
        this._currentLine = this.createLine(point, point);
      }

      //command
      return this._currentLine.end;
    } else {
      if (closesPoint) {
        this.disposeItem(this._currentLine.end);
        this._currentLine.end = closesPoint;

        fixRoomClockwise(this._currentLine);

        this._createPolygonStartingFromLine(this._currentLine);
        this._currentLine = undefined;
      } else {
        this._currentLine.end.point = this.getSnappedPoint(
          point,
          this._currentLine.end as Corner
        );
        this._currentLine = this.createLine(this._currentLine.end, point);
        return this._currentLine.end;
      }
    }
  }

  private _createPolygonStartingFromLine(from: Line) {
    const lines = this.searchPolygonPoints(from.start, from.end, [from]);

    const polygon = this.createPolygonFromLines(lines);
    if (this._onPolygonCrate) {
      this._onPolygonCrate(polygon);
    }
  }

  startLine(
    position: Vector3,
    drawer?: (start: Point, end: Point) => Line
  ): Corner | undefined {
    const closest = this.getClosestCorner(position);

    if (closest) {
      if (closest.countChildsOf(Balcony, Wall, BlankWall) >= 2) {
        return closest as Corner | undefined;
      } else {
        const closestsCurrentLine = closest.findFirstChildOf(
          Balcony,
          Wall,
          BlankWall
        )! as Balcony | Wall | BlankWall;
        position = this.getGridPoint(position);
        const corner = this._createCorner(position);
        if (closest === closestsCurrentLine.end) {
          this._currentLine = this.createLine(closest, corner, drawer);
          return corner;
        } else {
          this._currentLine = this.createLine(corner, closest, drawer);
          return corner as Corner;
        }
      }
    }
    //position = this.getGridPoint(position);
    const corner = this._createCorner(position);
    if (closest) {
      this._currentLine = this.createLine(closest, corner, drawer);
    } else {
      const start = this._createCorner(position);
      this._currentLine = this.createLine(start, corner, drawer);
    }
    return corner;
  }

  dropCorner(dropped: Corner) {
    const line = dropped.joints
      ?.filter((j) => j instanceof Line)
      .find((j: any) => j.end === dropped || j.start === dropped) as Line;

    const closest = this.getClosestCorner(dropped.point, line) as Corner;

    if (closest && line) {
      const closestsWallCount = closest.countChildsOf(Wall, Balcony, BlankWall);
      if (closestsWallCount >= 2) {
        this.disposeItem(line, dropped);
      } else {
        this.disposeItem(dropped);
        if (line.start === dropped) {
          if (isStartOfALine(closest)) {
            reverseAllFollowingWalls(closest);
          }
          line.start = closest;
          line.isDirty = true;
        } else {
          if (isEndOfALine(closest)) {
            reverseAllPreviousWalls(closest);
          }
          line.end = closest;
          line.isDirty = true;
        }
        if (isPartOfARoom(line)) {
          fixRoomClockwise(line);
          this._createPolygonStartingFromLine(line);
        }
      }
    } else {
      dropped.point = this.getSnappedPoint(dropped.point, dropped);
    }
  }

  movePoint(target: Vector3, attachedTo?: Corner) {
    if (attachedTo) {
      attachedTo.point =
        this.getClosestCorner(target, [attachedTo])?.point ||
        this.getSnappedPoint(target, attachedTo);
    }
  }

  private getUnfinishedCorners(): Corner[] {
    const corners = this.childs?.filter((c) => c.isOfType(Corner));
    corners?.forEach((c) => (c as Corner).label?.dispose());
    return corners?.filter(
      (c) => c.countChildsOf(Wall, Balcony, BlankWall) < 2
    ) as Corner[];
  }

  updateUnfinishedCorners() {
    const corners = this.getUnfinishedCorners();
    Engine.activeEngine.dispatchCustomEvent("unfinished-corners", {
      detail: corners,
    });
  }

  searchPolygonPoints(
    start: Point,
    checkPoint: Point,
    visitedLines: Line[] = []
  ): Line[] | undefined {
    const lines = start.joints?.filter((j) => {
      return j.isConstruct && !visitedLines.includes(j as Line);
    });

    const checkLineFound = lines?.find((l) => {
      const li = l as unknown as Line;
      return li.end == checkPoint || li.start == checkPoint;
    });

    if (checkLineFound) {
      visitedLines.push(checkLineFound as unknown as Line);
      return visitedLines;
    }

    const returns = lines?.map((l) => {
      const li = l as unknown as Line;
      const p = li.start == start ? li.end : li.start;
      visitedLines.push(li);
      return this.searchPolygonPoints(p, checkPoint, visitedLines);
    });

    return returns && returns.length > 0
      ? returns?.reduce((acc: any, current: any) => {
          return acc.length < current.length ? acc : current;
        })
      : [];
  }

  disposeItem(...items: Point[]) {
    items.forEach((it) => {
      const joints = [...(it.joints ?? [])];
      joints.forEach((j) => j.unbindJoint(it));
      it.joints = joints;
      it.removeFromParent();
    });

    items.forEach((item) => {
      if (this._childs?.includes(item)) {
        this._childs = this._childs.filter((child) => child != item);
      }
      const joints = item.joints;
      item.dispose();
      joints?.forEach((j) => {
        if (
          (j.joints && j.joints.length == 0) ||
          j.joints?.every((ij) => ij instanceof Meter)
        )
          this.disposeItem(j);
      });
    });
  }

  getAlignedPoint(point: Vector3, point2: Vector3) {
    const startPoint = point2;
    const lineVector = point.clone().sub(startPoint);

    const lineLength = lineVector.length();
    if (lineLength == 0) return point;

    const lineDirection = lineVector.normalize();

    const xAxis = new Vector3(1, 0, 0);
    let cos = xAxis.dot(lineDirection);
    if (Math.abs(cos) > 0.999)
      return startPoint.add(xAxis.multiplyScalar(Math.sign(cos) * lineLength));

    const zAxis = new Vector3(0, 0, 1);
    cos = zAxis.dot(lineDirection);
    if (Math.abs(cos) > 0.999)
      return startPoint.add(zAxis.multiplyScalar(Math.sign(cos) * lineLength));

    return point;
  }

  getGridPoint(point: Vector3) {
    const camera = Engine.activeEngine.activeCamera;
    const girdSystem = Engine.activeEngine.getSystem("GridSystem");
    if (!(camera?.instance instanceof OrthographicCamera)) return point;
    return girdSystem.checkGridPoint(point);
  }

  getSnappedPoint(point: Vector3, corner: Corner) {
    const corners = this.childs?.filter(
      (e) => e instanceof Corner && e != corner
    );

    let i = 0;
    let resultPoint = point.clone();

    //resultPoint = this.getGridPoint(resultPoint);
    while (corners && i < corners?.length) {
      const startPoint = corners[i++].point.clone();
      resultPoint = this.getAlignedPoint(resultPoint, startPoint);
    }

    return resultPoint;
  }

  getClosestCorner(
    point: Vector3,
    exlude?: Corner[] | Line | Polygon
  ): Corner | Point | undefined {
    let corners = this.getFilterJoints(exlude);

    let closest = 0.4;
    let closestCorner = undefined;

    corners?.forEach((corner) => {
      let distance = point.distanceTo(corner.point);
      if (distance < closest && distance < 0.4) {
        closest = distance;
        closestCorner = corner;
      }
    });

    return closestCorner;
  }

  getFilterJoints(exlude?: Corner[] | Line | Polygon) {
    let aeclude: any = exlude;
    if (exlude && (exlude instanceof Line || exlude instanceof Polygon)) {
      aeclude = exlude.joints;
    }
    return this._childs?.filter((child) => {
      return child instanceof Corner && !aeclude?.includes(child);
    });
  }

  clear = () =>
    new Promise<void>((resolve) => {
      this._childs?.forEach((c) => {
        c.dispose();
      });
      this._childs = [];
      this._models.forEach((m) => {
        m.clear();
        m.removeFromParent();
      });
      this._models = [];
      this._raycastingTargets = [];
      // TODO: model loader
      clearCache();
      clearAssetCache();
      this.updateUnfinishedCorners();
      resolve();
    });

  load(data: SceneImportData) {
    this._clearShapeChildren();
    if (!data.points?.length && !data.lines?.length) {
      this.updateUnfinishedCorners();
      return;
    }
    this._buildShapeFromData(
      { points: data.points ?? [], lines: data.lines ?? [] },
      data.productInstances,
    );
    this.updateUnfinishedCorners();
  }

  /**
   *
   */
  set renderMode(value: string) {
    super.renderMode = value;
    (Engine.activeEngine.activeScene as any).renderMode = value;
    this.childs?.forEach((p) => {
      p.renderMode = this._renderMode;
    });
  }

  render(scene?: Scene, camera?: CameraInterface) {
    this._childs?.forEach((child) => {
      if (child.isDirty) child.render(scene, camera);
    });
  }

  /**
   * read and write
   */
  export(): SceneInfo {
    const corners = this._childs
      ?.filter((c) => c instanceof Corner)
      .map((c) => {
        return {
          name: c.name,
          x: c.point.x,
          y: c.point.y,
          z: c.point.z,
        };
      });

    const walls =
      this._childs
        ?.filter((c) => c instanceof Line)
        .map((line) => ({
          start: corners!.findIndex((co) => co.name == line.start.name),
          end: corners!.findIndex((co) => co.name == line.end.name),
          type: ClassRegistry.getNameOf(line.constructor)!,
          hidden: line.hidden,
          uuid: line.name,
          attributes: {},
        })) || [];

    const exportModels = uniqueSugarModels(
      this._models.flatMap((m) => {
        if (m instanceof SugarModelConfigurator)
          return collectConfiguratorLeafModels(m);
        if (m instanceof SugarModel) return [m];
        return [];
      }),
    );

    const modelProducts = exportModels
      .map((m) => {
        return m.data;
      })
      .filter((m: any, i: number, self: any[]) => {
        return (
          i ===
          self.findIndex((t: any) => {
            return t.id === m.id;
          })
        );
      });

    const modelInstances = exportModels.map((m) => {
      const position = new Vector3();
      m.getWorldPosition(position);

      const quaternion = new Quaternion();
      m.getWorldQuaternion(quaternion);
      const rotation = new Euler().setFromQuaternion(quaternion);

      return {
        model: m.data.id,
        stateUuid: m.uuid,
        position: {
          x: position.x,
          y: position.y,
          z: position.z,
        },
        rotation: {
          isEuler: true,
          x: rotation.x,
          y: rotation.y,
          z: rotation.z,
        },
      };
    });

    const balconyObjects = this._childs?.filter((c) => c instanceof Balcony);

    const {
      profileProductDatas,
      glassProductDatas,
      profileProductInstances,
      glassProductInstances,
    } = balconyObjects?.reduce(
      (
        acc: {
          profileProductDatas: any[];
          glassProductDatas: any[];
          profileProductInstances: any[];
          glassProductInstances: any[];
        },
        balcony: Balcony
      ) => {
        balcony.export(acc, walls);
        return acc;
      },
      {
        profileProductDatas: [],
        glassProductDatas: [],
        profileProductInstances: [],
        glassProductInstances: [],
      }
    ) || {
      profileProductDatas: [],
      glassProductDatas: [],
      profileProductInstances: [],
      glassProductInstances: [],
    };

    walls.forEach((w) => {
      delete (w as any).uuid;
    });

    const products = modelProducts.concat(
      profileProductDatas,
      glassProductDatas
    );
    const productInstances = modelInstances.concat(
      profileProductInstances,
      glassProductInstances
    );

    return {
      name: this.name,
      products: products,
      productInstances: productInstances,
      shapes: {
        corners: corners || [],
        walls: walls || [],
      },
      stateSlices: captureDocument(),
    };
  }

  async import(data: SceneInfo) {
    await this.clear();
    const corners = data.shapes?.corners || DEFAULT_ROOM.points;
    const walls = data.shapes?.walls || DEFAULT_ROOM.lines;

    this.load({
      points: corners,
      lines: walls,
      products: data.products || [],
      productInstances: data.productInstances,
    });

    this._childs?.forEach((c) => {
      c.isDirty = true;
    });

    if (data.stateSlices) {
      applyDocument(data.stateSlices, "redo");
    }
  }
}
